<script setup lang="ts">
const { $serverApi } = useNuxtApp();
const { data: featuredContent } = await useAsyncData("home-featured", () =>
  $serverApi("/api/featured"),
  {
    default: () => [],
    server: false,
  },
);
const currentImageIndex = ref(0);
let intervalId: ReturnType<typeof setInterval>;
let counterId: ReturnType<typeof setInterval>;
const showArrows = ref(false);
const counter = ref(0);

const featuredContentLength = computed(
  () => (featuredContent.value && featuredContent.value.length) || 0
);

const toggleShowArrows = (toggle: string) => {
  showArrows.value = toggle === "show";
};

const resetInterval = () => {
  clearInterval(intervalId);
  clearInterval(counterId);
  counter.value = 0;
  intervalId = setInterval(() => {
    currentImageIndex.value =
      (currentImageIndex.value + 1) % featuredContentLength.value;
    counter.value = 0;
  }, 10000);
  counterId = setInterval(() => {
    counter.value++;
  }, 100);
};

const previousImage = () => {
  currentImageIndex.value =
    (currentImageIndex.value - 1 + featuredContentLength.value) %
    featuredContentLength.value;
  resetInterval();
};

const nextImage = () => {
  currentImageIndex.value =
    (currentImageIndex.value + 1) % featuredContentLength.value;
  resetInterval();
};

const goToImage = (index: number) => {
  currentImageIndex.value = index;
  resetInterval();
};

let touchStartX = ref(0);
let touchEndX = ref(0);

const handleTouchStart = (event: TouchEvent) => {
  touchStartX.value = event.touches[0].clientX;
};

const handleTouchMove = (event: TouchEvent) => {
  touchEndX.value = event.touches[0].clientX;
};

const handleTouchEnd = () => {
  let diffX = touchStartX.value - touchEndX.value;
  let threshold = 100;

  if (diffX > threshold) {
    nextImage();
  } else if (diffX < -threshold) {
    previousImage();
  }
};

onMounted(async () => {
  intervalId = setInterval(() => {
    currentImageIndex.value =
      (currentImageIndex.value + 1) % featuredContentLength.value;
    counter.value = 0;
  }, 10000);
  counterId = setInterval(() => {
    counter.value++;
  }, 100);
});

onUnmounted(() => {
  clearInterval(intervalId);
  clearInterval(counterId);
});
</script>
<template>
  <div
    v-if="featuredContent && featuredContentLength > 0"
    class="relative rounded-t-lg flex items-center overflow-hidden"
    @mouseover="toggleShowArrows('show')"
    @mouseleave="toggleShowArrows('hide')">
    <div
      v-if="featuredContentLength > 1"
      class="group z-20 mx-4 absolute w-14 h-14 bg-black/50 hidden sm:flex items-center justify-center cursor-pointer transition-all duration-300 ease-in-out border-[3px] border-neutral-300 rounded-full hover:border-primary-500"
      :class="showArrows ? 'opacity-100' : 'opacity-0'"
      @click="previousImage"
      @touchstart.prevent="previousImage">
      <BaseIconGrayArrow :thin="true" :vertical="true" class="mr-2" />
    </div>
    <div
      v-if="featuredContentLength > 1"
      class="group z-20 mx-4 absolute right-0 w-14 h-14 bg-black/60 hidden sm:flex items-center justify-center cursor-pointer transition-all duration-300 ease-in-out border-[3px] border-neutral-300 rounded-full hover:border-primary-500"
      :class="showArrows ? 'opacity-100' : 'opacity-0'"
      @click="nextImage"
      @touchstart.prevent="nextImage">
      <BaseIconGrayArrow
        :thin="true"
        :vertical="true"
        class="ml-2 -rotate-180" />
    </div>
    <transition
      mode="out-in"
      enter-active-class="ease-out duration-200"
      enter-from-class="opacity-20"
      enter-to-class="opacity-100"
      leave-active-class="ease-in duration-200"
      leave-from-class="opacity-100"
      leave-to-class="opacity-20">
      <img
        rel="preload"
        fetchPriority="high"
        :key="currentImageIndex"
        :src="featuredContent[currentImageIndex].image"
        alt="Hero Image"
        class="peer h-[603px] w-full object-cover rounded-t-lg"
        @touchstart="handleTouchStart"
        @touchmove="handleTouchMove"
        @touchend="handleTouchEnd" />
    </transition>
    <transition
      mode="out-in"
      enter-active-class="ease-out duration-200"
      enter-from-class="opacity-20"
      enter-to-class="opacity-100"
      leave-active-class="ease-in duration-200"
      leave-from-class="opacity-100"
      leave-to-class="opacity-20">
      <div
        :key="currentImageIndex"
        class="w-full absolute bottom-24 left-1/2 -translate-x-1/2 flex flex-col items-center justify-center gap-4">
        <h1
          class="text-center text-3xl md:text-6xl font-bold text-neutral-200 drop-shadow-[0px_4px_10px_rgba(0,0,0,0.7)]">
          {{ $t(`${featuredContent[currentImageIndex].title}`) }}
        </h1>
        <p
          class="text-center text-lg md:text-3xl font-bold drop-shadow-[0px_4px_10px_rgba(0,0,0,0.7)] text-rarity-legendary"
          :style="{
            color: `${featuredContent[currentImageIndex].descriptionColor}`,
          }">
          {{ $t(`${featuredContent[currentImageIndex].description}`) }}
        </p>
        <NuxtLinkLocale
          :data-test="
            'link-image-carousel-' +
            featuredContent[currentImageIndex].buttonText
          "
          :to="featuredContent[currentImageIndex].route">
          <BaseButtonSubmit
            :text="$t(`${featuredContent[currentImageIndex].buttonText}`)"
            data-test="button-image-carousel"
            size="lg"
            class="w-[320px] mt-4 drop-shadow-[0px_4px_10px_rgba(0,0,0,0.4)]" />
        </NuxtLinkLocale>
      </div>
    </transition>
    <div class="peer absolute bottom-9 w-full cursor-pointer">
      <div v-if="featuredContentLength > 1" class="flex justify-center gap-2">
        <div
          v-for="(_content, index) in featuredContent"
          :key="index"
          class="w-24 h-1 rounded-full bg-neutral-300"
          :class="{ 'bg-primary-500': index === currentImageIndex }"
          @click="goToImage(index)"></div>
      </div>
    </div>
    <!-- cool loading bar preserved in memoriam -->
    <!-- <div
      class="absolute bottom-0 h-1 bg-primary-500 rounded-b-lg transition-all ease-in-out duration-400 will-change-transform"
      :class="flipBar ? 'right-0' : ''"
      :style="{
        width: (flipBar ? 100 - counter : (1 / 100) * 100 * counter) + '%',
      }"></div> -->

    <div
      v-if="featuredContentLength > 1 && counter !== 0"
      class="absolute bottom-0 h-1 bar bg-primary-500 transition-all ease-in-out duration-400 will-change-transform"></div>
  </div>
  <div v-else class="my-16 lg:mt-40 lg:mb-24">
    <h2
      class="mx-auto max-w-2xl text-center text-4xl font-bold tracking-tight text-neutral-200">
      {{ $t("main.home.title") }}
    </h2>
    <p
      class="mx-auto mt-2 max-w-xl text-center text-2xl font-bold leading-8 text-neutral-300">
      {{ $t("main.home.subtitle") }}
    </p>
  </div>
</template>

<style scoped>
.bar {
  width: calc(100% / 100 * (v-bind("counter")));
}
</style>
